import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useClientLockForClient } from "../../hooks/useClientLock";
import ClientLockService from "../../lib/ClientLockService";
import { Lock, Unlock, AlertTriangle } from "lucide-react";
import { useToast } from "../ui/use-toast";
import { useAuth } from "../../lib/AuthContext";

interface ClientEditLockManagerProps {
  onLockAcquired?: () => void;
  onLockLost?: () => void;
}

const ClientEditLockManager: React.FC<ClientEditLockManagerProps> = ({
  onLockAcquired,
  onLockLost,
}) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [lockAcquired, setLockAcquired] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [hasAttemptedLock, setHasAttemptedLock] = useState(false);
  const auth = useAuth();

  // Check if client locking feature is enabled via database feature flag
  const isClientLockingEnabled =
    auth?.featureFlags?.includes("client_lock") || false;

  // Refs to manage state during unmount
  const lockStateRef = React.useRef({ id: "", acquired: false });
  const hasUnlockedRef = React.useRef(false);
  const isMountedRef = React.useRef(true);

  // Debug: Log component mount and feature flag status
  useEffect(() => {
    console.log("🎯 ClientEditLockManager mounted for client:", id);
    console.log("🚩 Feature flag status:", {
      isClientLockingEnabled,
      featureFlags: auth?.featureFlags,
    });
  }, []);

  const {
    isLocked,
    isLockedByMe,
    lockInfo,
    lockClient,
    unlockClient,
    isLocking,
    lockError,
  } = useClientLockForClient(id || "");

  // If client locking is disabled, immediately call onLockAcquired
  useEffect(() => {
    if (!isClientLockingEnabled) {
      setLockAcquired(true);
      onLockAcquired?.();
    }
  }, [isClientLockingEnabled, onLockAcquired]);

  // Attempt to acquire lock when component mounts (only if feature is enabled)
  // This effect should only run ONCE on mount, not on every state change
  useEffect(() => {
    if (!id || !isClientLockingEnabled || hasAttemptedLock) return;

    const acquireLock = async () => {
      if (!isMountedRef.current) return;
      setHasAttemptedLock(true);

      try {
        console.log(
          `🔒 Lock Check - isLocked: ${isLocked}, isLockedByMe: ${isLockedByMe}, lockInfo:`,
          lockInfo
        );

        // If already locked by current user, just set as acquired
        if (isLockedByMe) {
          if (!isMountedRef.current) return;
          setLockAcquired(true);
          onLockAcquired?.();
          console.log("✅ Client already locked by current user");
          return;
        }

        // Try to acquire lock first (this will check if it's locked by someone else)
        const success = await lockClient();

        if (!isMountedRef.current) return; // Check if still mounted after async

        if (success) {
          setLockAcquired(true);
          onLockAcquired?.();
          console.log("✅ Lock acquired successfully");
          toast({
            title: "Client Lock Acquired",
            description:
              "You now have exclusive editing rights for this client.",
          });
        } else {
          // Lock acquisition failed - someone else has it
          console.log("❌ Lock acquisition failed - locked by another user");
          setIsRedirecting(true);
          toast({
            title: "Client is Locked",
            description: `This client is being edited by ${lockInfo?.userName || "another user"}. Redirecting...`,
            variant: "destructive",
          });

          setTimeout(() => {
            if (isMountedRef.current) {
              navigate("/clients");
            }
          }, 2000);
        }
      } catch (error) {
        console.error("Error acquiring lock:", error);
        // On error, allow editing but show warning
        if (isMountedRef.current) {
          setLockAcquired(true);
          onLockAcquired?.();
        }
      }
    };

    acquireLock();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isClientLockingEnabled, hasAttemptedLock]);

  // Handle lock loss - if someone else takes the lock while you're on the page
  useEffect(() => {
    if (lockAcquired && isLocked && !isLockedByMe) {
      if (!isMountedRef.current) return;

      setLockAcquired(false);
      setIsRedirecting(true);
      onLockLost?.();
      toast({
        title: "Lock Lost",
        description:
          "Another user has acquired the lock for this client. Redirecting...",
        variant: "destructive",
      });

      // Redirect back to clients page when lock is lost
      setTimeout(() => {
        if (isMountedRef.current) {
          navigate("/clients");
        }
      }, 2000);
    }
  }, [lockAcquired, isLocked, isLockedByMe, onLockLost, toast, navigate]);

  // Update ref when lock is acquired
  useEffect(() => {
    if (lockAcquired && id) {
      lockStateRef.current = { id, acquired: true };
      hasUnlockedRef.current = false; // Reset unlock flag
      console.log("📌 Lock state stored in ref:", lockStateRef.current);
    }
  }, [lockAcquired, id]);

  // SINGLE cleanup effect - Release lock when component unmounts
  useEffect(() => {
    console.log(`🎯 ClientEditLockManager mounted for client ${id}`);
    isMountedRef.current = true;

    // Return cleanup function that will run when component unmounts
    return () => {
      isMountedRef.current = false; // Mark as unmounting

      // Use ref to get the latest lock state (avoids stale closure)
      const { id: lockedId, acquired } = lockStateRef.current;

      console.log(`🔚 ClientEditLockManager UNMOUNTING`, {
        lockedId,
        acquired,
        hasUnlocked: hasUnlockedRef.current,
      });

      // Skip if already unlocked or never acquired
      if (!acquired || !lockedId || hasUnlockedRef.current) {
        console.log("🔓 Skipping unlock - already handled");
        return;
      }

      console.log(`🔓 Unmount cleanup - unlocking client ${lockedId}`);

      try {
        const service = ClientLockService.getInstance();
        if (!service.isServiceInitialized()) {
          console.warn("⚠️ Service not initialized, cannot unlock");
          return;
        }

        // Call sync unlock immediately - don't wait for response
        service.unlockClientSync(lockedId);
        hasUnlockedRef.current = true;
        console.log(`✅ Client ${lockedId} unlock initiated on unmount`);
      } catch (error) {
        console.error("❌ Error during unmount unlock:", error);
      }
    };
  }, [id]); // Only depend on id - ref handles state changes

  // If client locking is disabled, don't render anything
  if (!isClientLockingEnabled) {
    return null;
  }

  // Show blocking overlay when redirecting
  if (isRedirecting) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]">
        <div className="bg-white p-6 rounded-lg shadow-xl max-w-md">
          <div className="flex items-center gap-3 mb-4">
            <AlertTriangle className="h-6 w-6 text-red-600" />
            <h3 className="text-lg font-semibold text-red-600">
              Client is Locked
            </h3>
          </div>
          <p className="text-gray-700 mb-2">
            This client is being edited by{" "}
            <strong>{lockInfo?.userName || "another user"}</strong>.
          </p>
          <p className="text-gray-600 text-sm">
            Redirecting you back to the clients list...
          </p>
          <div className="mt-4 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
          </div>
        </div>
      </div>
    );
  }

  // Show warning banner if somehow on page when locked (shouldn't happen with redirect)
  if (isLocked && !isLockedByMe) {
    return (
      <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-red-600" />
          <span className="text-sm text-red-600 font-medium">
            This client is being edited by{" "}
            {lockInfo?.userName || "another user"}
          </span>
        </div>
        <p className="text-xs text-red-500 mt-1">
          Redirecting you back to clients list...
        </p>
      </div>
    );
  }

  // Log errors to console instead of showing in UI
  if (lockError) {
    console.error("Client lock error:", lockError);
    // Don't show error in UI, just continue
  }

  // Show lock status indicator
  return (
    <div className="mb-4">
      <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
        {isLockedByMe ? (
          <>
            <Lock className="h-4 w-4 text-green-600" />
            <span className="text-sm text-green-600 font-medium">
              You are editing this client
            </span>
          </>
        ) : (
          <>
            <Unlock className="h-4 w-4 text-gray-400" />
            <span className="text-sm text-gray-500">No active lock</span>
          </>
        )}

        {isLocking && (
          <span className="text-sm text-blue-600">Acquiring lock...</span>
        )}
      </div>
    </div>
  );
};

export default ClientEditLockManager;
